/**
 * 工程部历史账号和业务单据可能保存英文部门简称；仅在部门字段展示时统一名称，
 * 避免改写账号标识、历史单据或误改工程师职称及其他部门的名称。
 */
const CONSTRUCTION_DEPARTMENT = /^(?:engineering|engineer|construction)(?:\s+(?:dept\.?|department))?$/i;

/** 有组织身份时优先依据中文名称或已知 ID 判断，不能把其他部门的同名英文误判为工程部。 */
function isConstructionDepartment(value, identity) {
  if (typeof value !== "string" || !CONSTRUCTION_DEPARTMENT.test(value.trim())) return false;
  if (identity == null) return true; // 旧业务自由文本没有身份信息，只能把完整的已知名称作为历史别名。
  const name = typeof identity.name === "string" ? identity.name.trim() : "";
  if (/[\u3400-\u9fff]/.test(name)) return name === "工程部";
  return identity.id === "engineering";
}

/** 返回部门的显示名称；提供组织 ID/中文名时先核实身份，自由文本保留既有别名兼容。 */
export function departmentLabel(value, identity) {
  return isConstructionDepartment(value, identity) ? "Construction" : value || "";
}

/** 显示名重合时附原值；如果自由文本恰好等于消歧后的名称，则明确标示每项完整原值。 */
function distinguishDepartmentOptions(options) {
  const counts = new Map();
  options.forEach(({ label }) => counts.set(label, (counts.get(label) || 0) + 1));
  const labelled = options.map((option) => ({
    ...option,
    label: counts.get(option.label) > 1 ? `${option.label} (${option.value})` : option.label,
  }));
  if (new Set(labelled.map(({ label }) => label)).size === labelled.length) return labelled;

  // 例如原值本身就是「Construction (engineering)」时，单纯加序号仍会误导筛选口径。
  // 此时列出完整原值，让用户能区分整串原值与 engineering；绝不改变实际选择值。
  const used = new Set();
  return options.map((option) => {
    const base = `${option.label} [original: ${JSON.stringify(option.value)}]`;
    let label = base;
    let suffix = 2;
    while (used.has(label)) label = `${base} [${suffix++}]`;
    used.add(label);
    return { ...option, label };
  });
}

/** 生成部门下拉项：保留原值；显示名重合时附原值，避免用户误选不同的历史筛选口径。 */
export function departmentOptions(values) {
  const seen = new Set();
  const options = (values || []).filter(Boolean).flatMap((value) => {
    if (seen.has(value)) return [];
    seen.add(value);
    return [{ value, label: departmentLabel(value) }];
  });
  return distinguishDepartmentOptions(options);
}

/** 组织部门按 ID 选择和配置权限；仅对显示名重合的选项补 ID，不改提交值或组织对象。 */
export function departmentIdentityOptions(departments, zh) {
  return distinguishDepartmentOptions((departments || []).map((department) => ({
    ...department,
    value: department.id,
    label: zh ? department.name : departmentLabel(department.nameEn || department.name, department),
  })));
}

/** 精确匹配当前原始值；显示名相同不能代表筛选值相同，自定义值仍由输入框展示。 */
export function selectedDepartmentOption(options, value) {
  return options.find((option) => option.value === value)?.value || "";
}
